/**
 * 信令连接缓存与会话持久化工具模块。
 */
import { useSessionStore } from "@/stores/remote";

const LAST_DEVICE_ID_KEY = "last_device_id";
const LAST_DEVICE_CODE_KEY = "last_device_code";
const LAST_LINK_VALUE_KEY = "last_link_value";

const CURRENT_DEVICE_ID_KEY = "current_device_id";
const CURRENT_DEVICE_CODE_KEY = "current_device_code";
const DEVICE_INFO_KEY = "device_info";
const SESSION_INFO_KEY = "session_info";
const SESSION_EXPIRES_AT_KEY = "session_expires_at";

type CachedConnection = {
	deviceId: string;
	passCode: string;
	link: string;
};

type ConnectionResult = {
	deviceInfo?: Record<string, unknown> | null;
	sessionInfo?: Record<string, unknown> | null;
	expiresAt?: string;
};

/**
 * 读取最近一次成功连接缓存，便于自动填充。
 */
export const loadCachedConnection = (): CachedConnection => ({
	deviceId: localStorage.getItem(LAST_DEVICE_ID_KEY) || "",
	passCode: localStorage.getItem(LAST_DEVICE_CODE_KEY) || "",
	link: localStorage.getItem(LAST_LINK_VALUE_KEY) || "",
});

/**
 * 缓存当前输入的设备信息，方便再次发起连接。
 * @param {string} deviceId 设备 ID
 * @param {string} passCode 访问码
 */
export const cacheConnection = (deviceId: string, passCode: string) => {
	localStorage.setItem(LAST_DEVICE_ID_KEY, deviceId);
	localStorage.setItem(LAST_DEVICE_CODE_KEY, passCode);

	const pathParts = window.location.pathname.split("/");
	const lastPart = pathParts[pathParts.length - 1];
	if (lastPart?.includes("=")) {
		localStorage.setItem(LAST_LINK_VALUE_KEY, window.location.href);
	}
};

/**
 * 将服务端返回的连接信息写入 sessionStorage，供后续页面使用。
 * @param {ConnectionResult} result 服务端返回信息
 * @param {string} deviceId 设备 ID
 * @param {string} passCode 访问码
 */
export const persistSession = (
	result: ConnectionResult,
	deviceId: string,
	passCode: string,
) => {
	const store = useSessionStore.getState();
	const deviceInfo = result.deviceInfo || null;
	const sessionInfo = result.sessionInfo || null;
	const expiresAt = result.expiresAt || "";

	sessionStorage.setItem(CURRENT_DEVICE_ID_KEY, deviceId);
	sessionStorage.setItem(CURRENT_DEVICE_CODE_KEY, passCode);

	if (deviceInfo) {
		sessionStorage.setItem(DEVICE_INFO_KEY, JSON.stringify(deviceInfo));
	} else {
		sessionStorage.removeItem(DEVICE_INFO_KEY);
	}

	if (sessionInfo) {
		sessionStorage.setItem(SESSION_INFO_KEY, JSON.stringify(sessionInfo));
	} else {
		sessionStorage.removeItem(SESSION_INFO_KEY);
	}

	if (expiresAt) {
		sessionStorage.setItem(SESSION_EXPIRES_AT_KEY, expiresAt);
	} else {
		sessionStorage.removeItem(SESSION_EXPIRES_AT_KEY);
	}

	store.setSessionPayload({
		deviceId,
		passCode,
		deviceInfo,
		sessionInfo,
		expiresAt,
	});
};

/**
 * 清理 sessionStorage 中的连接信息，确保状态复位。
 */
export const clearSession = () => {
	const store = useSessionStore.getState();

	sessionStorage.removeItem(CURRENT_DEVICE_ID_KEY);
	sessionStorage.removeItem(CURRENT_DEVICE_CODE_KEY);
	sessionStorage.removeItem(DEVICE_INFO_KEY);
	sessionStorage.removeItem(SESSION_INFO_KEY);
	sessionStorage.removeItem(SESSION_EXPIRES_AT_KEY);

	store.clearSession();
};
